import {BaseComponent} from '../base/component.base.js';
import template from './notification.template.html';
import styles from './notification.styles.css';
import {NotificationType} from '../../core/vocabulary.js';

/** Font Awesome icon per toast type, so the type never depends on colour alone. */
const ICONS = Object.freeze({
    [NotificationType.SUCCESS]: 'fa-circle-check',
    [NotificationType.ERROR]: 'fa-circle-exclamation',
    [NotificationType.WARNING]: 'fa-triangle-exclamation',
});

/**
 * A single toast. Success and warning toasts hide themselves after a reading time that
 * grows with the message; an error stays until the user closes it (with its close button or
 * Escape) or until the app reports that a later action succeeded, so it cannot vanish before
 * it has been read. A new toast replaces the current one.
 */
export class NotificationComponent extends BaseComponent {
    #timeout = null;

    /**
     * Escape closes the toast. It listens on the window, after every document listener, so a
     * menu that handles the same Escape (and cancels the event) closes first on its own, and an
     * open dialog keeps Escape to itself.
     * @param {KeyboardEvent} event
     */
    #onKeydown = (event) => {
        if (event.key !== 'Escape' || event.defaultPrevented || !this.#visible()) return;
        if (event.composedPath().some((node) => node instanceof HTMLDialogElement && node.open)) return;
        this.hide();
    };

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.$('#btn-close').addEventListener('click', () => this.hide());
        window.addEventListener('keydown', this.#onKeydown);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('keydown', this.#onKeydown);
        clearTimeout(this.#timeout);
        this.#timeout = null;
    }

    /**
     * @param {string} message
     * @param {NotificationType} type
     * @param {{durationMs?: number}} [options] `durationMs` also makes an error hide itself
     */
    show(message, type, options = {}) {
        const toast = this.$('#toast');
        const msg = this.$('#message');
        if (!toast || !msg) return;

        clearTimeout(this.#timeout);
        this.#timeout = null;
        toast.className = type;
        msg.textContent = message;
        this.$('#icon').className = `toast-icon fa-solid ${ICONS[type] ?? ICONS[NotificationType.WARNING]}`;

        if (type === NotificationType.ERROR && options.durationMs === undefined) {
            return;
        }
        // Longer messages need more reading time. At about 20 characters a second, 5s covers
        // ~100 characters; every ~30 characters beyond that add another second.
        const baseMs = 5000;
        const extra = Math.max(0, Math.floor((message.length - 80) / 30)) * 1000;
        const durationMs = options.durationMs ?? Math.min(8000, baseMs + extra);

        this.#timeout = setTimeout(() => this.hide(), durationMs);
    }

    /** Hides the toast when it shows an error, after a later action succeeded; other toasts stay. */
    dismissError() {
        if (this.$('#toast')?.className === NotificationType.ERROR) {
            this.hide();
        }
    }

    /** @returns {boolean} whether a toast is on screen */
    #visible() {
        const toast = this.$('#toast');
        return Boolean(toast) && toast.className !== 'hidden';
    }

    /** Hides the current toast, if any. */
    hide() {
        clearTimeout(this.#timeout);
        this.#timeout = null;
        const toast = this.$('#toast');
        if (toast) toast.className = 'hidden';
    }
}

customElements.define('user-notification', NotificationComponent);
